// components/AnimatedImage.tsx
import React from "react";
import {
  motion,
  useAnimation,
  useInView,
  useReducedMotion,
} from "framer-motion";

type AnimatedImageProps = {
  src: string;
  alt: string;
  /** Tailwind aspect class, t.ex. "aspect-[16/9]" eller "aspect-square" */
  aspectClassName?: string;
  /** Extra wrapper-klasser (runt bilden) */
  wrapperClassName?: string;
  /** Extra klasser direkt på <img> */
  imgClassName?: string;
  /** Rundning (Tailwind) – default "rounded-lg" */
  roundedClassName?: string;
  /** Lazy/eager */
  loading?: "lazy" | "eager";
  /** fetchPriority-hint */
  fetchPriority?: "high" | "low" | "auto";
  /** InView: kör bara en gång? */
  once?: boolean;
  /** InView-tröskel (0..1) */
  amount?: number;
  /** Överstyr transition (annars snygg default) */
  transition?: Parameters<typeof motion.img>["0"]["transition"];
  /** Visa gradient-overlay ovanpå bilden */
  withOverlay?: boolean;
  /** Klasser för overlay (Tailwind) */
  overlayClassName?: string;
  /** Klickhanterare */
  onClick?: () => void;
};

export const AnimatedImage: React.FC<AnimatedImageProps> = ({
  src,
  alt,
  aspectClassName = "aspect-[16/9]",
  wrapperClassName = "",
  imgClassName = "",
  loading = "lazy",
  fetchPriority = "auto",
  once = true,
  amount = 0.35,
  transition,
  withOverlay = false,
  overlayClassName = "from-black/0 via-black/0 to-black/30",
  onClick,
}) => {
  const controls = useAnimation();
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const inView = useInView(wrapperRef, { once, amount });
  const prefersReducedMotion = useReducedMotion();

  // Starta animation när i vy
  React.useEffect(() => {
    if (prefersReducedMotion) {
      controls.set({ opacity: 1, y: 0, scale: 1 });
      return;
    }
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: transition ?? {
          type: "spring",
          stiffness: 200,
          damping: 22,
        },
      });
    }
  }, [inView, prefersReducedMotion, controls, transition]);

  // Fallback på bildens onLoad (ifall något råkat sätta opacity 0)
  const handleLoad = () => {
    controls.start({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: transition ?? { type: "spring", stiffness: 200, damping: 22 },
    });
  };

  return (
    <div
      ref={wrapperRef}
      className={`${wrapperClassName} relative w-full overflow-hidden ${aspectClassName} `}
      onClick={onClick}
    >
      <motion.img
        src={src}
        alt={alt}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={controls}
        onLoad={handleLoad}
        className={`${imgClassName} h-full w-full object-contain`}
      />

      {withOverlay && (
        <div
          className={`${overlayClassName} pointer-events-none absolute inset-0 bg-gradient-to-b `}
        />
      )}
    </div>
  );
};
